import { ofetch } from 'ofetch';
import { log } from '@shared/log.browser.ts';
import { createTileListOnEx } from '@tilelist/application/core/OnExFactory.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import type { TileListPageSession } from '@tilelist/application/core/TileListSession.ts';
import { createPageSession } from '@tilelist/application/core/TileListSession.ts';
import type { ClientReloadContextProvider } from '@tilelist/application/ports/ClientReloadContextProvider.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { SelectionEvents } from '@tilelist/application/ports/SelectionEvents.ts';
import type { TileListPageFetcher } from '@tilelist/application/ports/TileListPageFetcher.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import { EvergladesClient } from '@tilelist/clients/EvergladesClient.ts';
import { FullTileListStrategy } from '@tilelist/domain/composition/strategies/FullTileListStrategy.ts';
import type { PageLoadStrategy } from '@tilelist/domain/composition/strategies/PageLoadStrategy.ts';
import type { TileItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import { createCacheKeyRefresher } from '@tilelist/services/cache/CacheKeyRefresher.browser.ts';
import { restoreBySelector, saveScrollSelector } from '@tilelist/services/cache/ScrollPositionCache.browser.ts';
import { maybeRestoreFromCache, saveToCache } from '@tilelist/services/cache/TileListCache.browser.svelte.ts';
import { createEventsIncoming } from '@tilelist/services/events/EventsIncoming.browser.ts';
import { createEventsOutgoing } from '@tilelist/services/events/EventsOutgoing.browser.ts';
import { createSelectionEvents } from '@tilelist/services/events/SelectionEvents.browser.ts';
import { getTrackingService, getVariationLoadingService } from '@tilelist/services/ServiceFactory.browser.ts';
import { trackAdjustForTileList } from '@tilelist/services/tracking/AppsTracking.browser.ts';
import { trackFirebase } from '@tilelist/services/tracking/FirebaseTracking.browser.ts';
import { frontendPageViewTracking } from '@tilelist/services/tracking/PageViewTracking.browser.ts';
import { createTrackingClientReloadContextProvider } from '@tilelist/services/TrackingClientReloadContextProvider.browser.ts';

export type StrategyFactory = (variationLoader: VariationLoader) => PageLoadStrategy;

export type TileListBrowserWiring = {
    session: TileListPageSession;
    eventsIncoming: EventsIncoming;
    eventsOutgoing: EventsOutgoing;
    selectionEvents: SelectionEvents;
    trackingService: TrackingPort;
    /*                                                                     */
    entryRule: string;
    variationLoadingService: VariationLoader;
    pageFetcher: TileListPageFetcher;
    reloadContextProvider: ClientReloadContextProvider;
    restoreScrollPosition(): Promise<void>;
    saveScrollSelector(): void;
    maybeRestoreFromCache(pageView: TileListPageLayout): Promise<TileListPageLayout>;
    sendTracking(pageView: TileListPageLayout, historic?: boolean): void;
    restoreAndTrackInitialPage(pageView: TileListPageLayout): Promise<TileListPageLayout>;
    onRedirectToPdp(item: TileItem, currentPageView: TileListPageLayout | null): void;
};

/**
 *
 *
 *
 *
 */
function readPersistedEntryRule(): string | undefined {
    const state: { entryRule?: string } | null = window.history.state;
    return state?.entryRule;
}

/**
 *
 *
 *
 *
 *
 */
export function createTileListBrowserWiring(
    initialSession: TileListInitialSession,
    navigationHistoryEnabled: boolean,
    strategyFactory?: StrategyFactory,
): TileListBrowserWiring {
    const variationLoadingService = getVariationLoadingService();
    const onex = createTileListOnEx(initialSession.session);
    const eventsOutgoing = createEventsOutgoing();
    const eventsIncoming = createEventsIncoming();
    const trackingService = getTrackingService();
    const reloadContextProvider = createTrackingClientReloadContextProvider(trackingService);

    /*                                                                   */
    /*                                                                 */
    const entryRule = readPersistedEntryRule() ?? initialSession.session.entryRule;
    const selectionEvents = createSelectionEvents(
        eventsIncoming,
        eventsOutgoing,
        onex.isToggleActive('REPTILE_1147_NEXUS_SELECTION'),
        {
            initialEntryRule: entryRule,
            currentUrl: () => window.location.href,
        },
    );

    const evergladesClient = new EvergladesClient({
        fetchClient: ofetch,
        logger: log,
        entryRule,
        timeout: 10000,
    });

    const strategy = strategyFactory
        ? strategyFactory(variationLoadingService)
        : new FullTileListStrategy(variationLoadingService);

    const session = createPageSession(initialSession, {
        pageFetcher: evergladesClient,
        variationLoadingService,
        reloadContextProvider,
        eventPublisher: eventsOutgoing,
        cacheKeyRefresher: createCacheKeyRefresher(),
        strategy,
    });

    function sendTracking(pageView: TileListPageLayout, historic?: boolean): void {
        const pageViewTracking = frontendPageViewTracking(!!historic);
        /*                                                                        */
        /*                                                     */
        trackingService.trackPageView(pageView, pageViewTracking, navigationHistoryEnabled ? entryRule : undefined);
        trackFirebase(eventsOutgoing, pageView.tileListItems);
        trackAdjustForTileList(pageView.tileListItems, session.onex);
    }

    async function restoreAndTrackInitialPage(pageView: TileListPageLayout): Promise<TileListPageLayout> {
        const restored = await maybeRestoreFromCache(pageView);
        sendTracking(restored);
        return restored;
    }

    function onRedirectToPdp(_item: TileItem, currentPageView: TileListPageLayout | null): void {
        saveScrollSelector();
        if (currentPageView) {
            void saveToCache(currentPageView);
        }
    }

    return {
        session,
        eventsIncoming,
        eventsOutgoing,
        selectionEvents,
        trackingService,
        entryRule,
        variationLoadingService,
        pageFetcher: evergladesClient,
        reloadContextProvider,
        restoreScrollPosition: restoreBySelector,
        saveScrollSelector,
        maybeRestoreFromCache,
        sendTracking,
        restoreAndTrackInitialPage,
        onRedirectToPdp,
    };
}
